import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './MouvementsStock.css';

interface Mouvement {
  id: number;
  produitId: number;
  produitNom: string;
  type: string;
  quantite: number;
  stockAvant: number;
  stockApres: number;
  boutique: string;
  boutiqueDest: string;
  reference: string;
  motif: string;
  utilisateur: string;
  createdAt: string;
}

const typeConfig: Record<string, { label: string; couleur: string; icone: string }> = {
  entree: { label: 'Entrée', couleur: '#3B6D11', icone: '⬆️' },
  sortie: { label: 'Sortie', couleur: '#A32D2D', icone: '⬇️' },
  vente: { label: 'Vente', couleur: '#185FA5', icone: '🛒' },
  transfert: { label: 'Transfert', couleur: '#854F0B', icone: '🔄' },
  ajustement: { label: 'Ajustement', couleur: '#534AB7', icone: '⚙️' },
  retour: { label: 'Retour', couleur: '#1D9E75', icone: '↩️' },
};

export default function MouvementsStock() {
  const [mouvements, setMouvements] = useState<Mouvement[]>([]);
  const [chargement, setChargement] = useState(true);
  const [filtreType, setFiltreType] = useState('tous');
  const [recherche, setRecherche] = useState('');

  useEffect(() => {
    chargerMouvements();
  }, []);

  const chargerMouvements = async () => {
    try {
      const res = await api.get('/produits/mouvements/historique');
      setMouvements(res.data);
    } catch (e) {
    } finally {
      setChargement(false);
    }
  };

  const mouvementsFiltres = mouvements.filter(m => {
    const matchType = filtreType === 'tous' || m.type === filtreType;
    const matchRecherche = recherche === '' ||
      m.produitNom.toLowerCase().includes(recherche.toLowerCase()) ||
      m.motif?.toLowerCase().includes(recherche.toLowerCase()) ||
      m.reference?.toLowerCase().includes(recherche.toLowerCase());
    return matchType && matchRecherche;
  });

  const statsTypes = Object.keys(typeConfig).reduce((acc: any, type) => {
    acc[type] = mouvements.filter(m => m.type === type).length;
    return acc;
  }, {});

  if (chargement) return <div className="page-chargement">Chargement de l'historique...</div>;

  return (
    <div className="mouvements-page">
      <div className="page-header">
        <div>
          <h1>Historique des mouvements de stock</h1>
          <p>Journal complet de toutes les entrées, sorties et transferts</p>
        </div>
        <button className="btn-secondary" onClick={chargerMouvements}>
          🔄 Actualiser
        </button>
      </div>

      {/* STATS PAR TYPE */}
      <div className="mvt-stats">
        {Object.entries(typeConfig).map(([type, cfg]) => (
          <div key={type}
            className={`mvt-stat-card ${filtreType === type ? 'active' : ''}`}
            onClick={() => setFiltreType(filtreType === type ? 'tous' : type)}>
            <div className="mvt-stat-icone">{cfg.icone}</div>
            <div className="mvt-stat-val" style={{ color: cfg.couleur }}>
              {statsTypes[type] || 0}
            </div>
            <div className="mvt-stat-lbl">{cfg.label}</div>
          </div>
        ))}
      </div>

      {/* FILTRES */}
      <div className="mvt-filtres">
        <input
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
          placeholder="🔍 Rechercher par produit, motif ou référence..."
          className="mvt-search"
        />
        <div className="filtre-type">
          <button className={filtreType === 'tous' ? 'active' : ''}
            onClick={() => setFiltreType('tous')}>
            Tous ({mouvements.length})
          </button>
          {Object.entries(typeConfig).map(([type, cfg]) => (
            <button key={type}
              className={filtreType === type ? 'active' : ''}
              onClick={() => setFiltreType(type)}>
              {cfg.icone} {cfg.label}
            </button>
          ))}
        </div>
      </div>

      {/* TABLEAU */}
      <div className="mvt-table">
        <table>
          <thead>
            <tr>
              <th>Date & Heure</th>
              <th>Produit</th>
              <th>Type</th>
              <th>Quantité</th>
              <th>Stock avant</th>
              <th>Stock après</th>
              <th>Détails</th>
            </tr>
          </thead>
          <tbody>
            {mouvementsFiltres.map(m => {
              const cfg = typeConfig[m.type] || typeConfig.ajustement;
              return (
                <tr key={m.id}>
                  <td className="mvt-date">
                    <div>{new Date(m.createdAt).toLocaleDateString('fr-FR')}</div>
                    <div className="mvt-heure">
                      {new Date(m.createdAt).toLocaleTimeString('fr-FR', {
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  </td>
                  <td>
                    <div className="mvt-produit">{m.produitNom}</div>
                    {m.reference && (
                      <div className="mvt-ref">Réf: {m.reference}</div>
                    )}
                  </td>
                  <td>
                    <span className="mvt-type-badge" style={{
                      background: cfg.couleur + '20',
                      color: cfg.couleur,
                    }}>
                      {cfg.icone} {cfg.label}
                    </span>
                  </td>
                  <td>
                    <span className="mvt-qte" style={{
                      color: ['entree', 'retour'].includes(m.type) ? '#3B6D11' : '#A32D2D'
                    }}>
                      {['entree', 'retour'].includes(m.type) ? '+' : '-'}{m.quantite}
                    </span>
                  </td>
                  <td className="mvt-stock">{m.stockAvant}</td>
                  <td className="mvt-stock">{m.stockApres}</td>
                  <td>
                    <div className="mvt-details">
                      {m.motif && <div>{m.motif}</div>}
                      {m.type === 'transfert' && m.boutique && (
                        <div className="mvt-transfert-info">
                          {m.boutique} → {m.boutiqueDest}
                        </div>
                      )}
                      {m.utilisateur && (
                        <div className="mvt-utilisateur">👤 {m.utilisateur}</div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {mouvementsFiltres.length === 0 && (
          <div className="table-empty">
            {recherche || filtreType !== 'tous'
              ? 'Aucun mouvement ne correspond aux critères'
              : 'Aucun mouvement de stock enregistré'}
          </div>
        )}
      </div>

      <div className="mvt-footer">
        {mouvementsFiltres.length} mouvement{mouvementsFiltres.length > 1 ? 's' : ''} affiché{mouvementsFiltres.length > 1 ? 's' : ''}
        {filtreType !== 'tous' && ` (filtre : ${typeConfig[filtreType]?.label})`}
      </div>
    </div>
  );
}